import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  Box,
  Grid,
  MenuItem,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Modal,
  IconButton,
  InputAdornment,
  CircularProgress,
  TablePagination,
  Container,
  Snackbar,
  Alert as MuiAlert,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Info as InfoIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import base64 from "base64-js";

// Define CustomAlert to avoid naming conflict with MuiAlert
const CustomAlert = React.forwardRef(function CustomAlert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const StudentUser = () => {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sections, setSections] = useState([]);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [editUserIndex, setEditUserIndex] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTablePassword, setShowTablePassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(15);
  const [authorization, setAuthorization] = useState([]);
  const [canDelete, setCanDelete] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const navigate = useNavigate();
  const { encodedFormId } = useParams();
  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Decode formId
  const decodeFormId = (encodedFormId) => {
    try {
      const bytes = base64.toByteArray(encodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };
  const formId = decodeFormId(encodedFormId);

  // Fetch permissions
  // const Authorizer = async () => {
  //   if (!formId) {
  //     setSnackbar({
  //       open: true,
  //       message: "Invalid form ID",
  //       severity: "error",
  //     });
  //     setLoading(false);
  //     return;
  //   }

  //   try {
  //     setLoading(true);
  //     const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: token,
  //       },
  //       body: JSON.stringify({ formId }),
  //     });

  //     if (response.ok) {
  //       const responseData = await response.json();
  //       setAuthorization(responseData);
  //       if (responseData.length > 0) {
  //         const authorizationData = responseData[0];
  //         setCanDelete(authorizationData.uDelete === 1);
  //         setCanEdit(authorizationData.uModify === 1);
  //       } else {
  //         setCanDelete(false);
  //         setCanEdit(false);
  //       }
  //     } else {
  //       console.error("Failed to fetch permissions");
  //       setSnackbar({
  //         open: true,
  //         message: "Failed to fetch permissions",
  //         severity: "error",
  //       });
  //       setCanDelete(false);
  //       setCanEdit(false);
  //     }
  //   } catch (error) {
  //     console.error("API request error:", error);
  //     setSnackbar({
  //       open: true,
  //       message: "Error fetching permissions",
  //       severity: "error",
  //     });
  //     setCanDelete(false);
  //     setCanEdit(false);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Toggle password visibility
  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Fetch Classes
  const fetchClasses = async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        throw new Error("Record Not Found");
      }
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  // Fetch Sections
  const fetchSections = async (selectedClassId) => {
    try {
      const response = await fetch(`${apiUrl}/Teacher/ddlSection_clsId`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          teacherId: sessionStorage.getItem("employeeId"),
          classId: selectedClassId,
        }),
      });
      const data = await response.json();
      setSections(data);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  // Fetch Users (Updated)
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/StudentUser/GetCreatedStudentUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          classId: classId || 0,
          sectionId: sectionId || 0,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setUsers(
            data.map((user) => ({
              ...user,
              active: user.userActive,
            }))
          );
        } else {
          setUsers([]);
          setSnackbar({
            open: true,
            message: "No User Found",
            severity: "error",
          });
        }
      } else {
        setUsers([]);
        setSnackbar({
          open: true,
          message: "Failed to fetch student users",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setSnackbar({
        open: true,
        message: "Error fetching student users",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (event) => {
    const selectedClassId = event.target.value;
    setClassId(selectedClassId);
    setSectionId("0"); // Reset section to "All" when class changes
    setPage(0);
    setSearch("");
  };

  const handleSectionChange = (event) => {
    setSectionId(event.target.value);
    setPage(0);
    setSearch("");
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  // Handle edit
  const handleEditClick = (index) => {
  setEditUserIndex(index);
  const user = users[index];

  let formattedDate = "";
  const raw = user.expiryDate;

  if (raw) {
    const str = String(raw).trim();

    // Matches "DD/MM/YYYY" optionally followed by " HH:MM:SS"
    const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (match) {
      const [, d, m, y] = match;
      formattedDate = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    } else {
      // Fallback for ISO-like strings, e.g. "2027-07-18"
      const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        formattedDate = `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
      }
    }
  }

  setEditedUser({ ...user, expiryDate: formattedDate });
  setShowModal(true);
  setSelectedUser({});
};

  const handleModalClose = () => {
    setShowModal(false);
    setEditUserIndex(null);
    setEditedUser({});
    setSelectedUser({});
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleEditChange = (field, value) => {
    setEditedUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (editUserIndex !== null) {
      if (
        editedUser.password &&
        (editedUser.password.length < 6 || editedUser.password.length > 12)
      ) {
        setSnackbar({
          open: true,
          message: "Password must be between 6 and 12 characters.",
          severity: "error",
        });
        return;
      }

      const userId = editedUser.userID;
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/CPanel/UserId?UserId=${userId}`;

      const formatDateToDDMMYYYY = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const payload = {
        userGroupId: editedUser.userGroupID || 0,
        password: editedUser.password || "",
        expiryDate: formatDateToDDMMYYYY(editedUser.expiryDate),
      };

      try {
        const response = await fetch(apiUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const updatedUsers = [...users];
          updatedUsers[editUserIndex] = {
            ...updatedUsers[editUserIndex],
            ...editedUser,
          };
          setUsers(updatedUsers);
          setSnackbar({
            open: true,
            message: "User updated successfully!",
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: "Error updating user.",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error updating user:", error);
        setSnackbar({
          open: true,
          message: "Error updating user.",
          severity: "error",
        });
      }
    }
    handleModalClose();
  };

  // Handle delete
  const handleShowDeleteModal = (user, index) => {
    setSelectedUser(user);
    setEditUserIndex(index);
    setShowModal(true);
  };

  const handleDeleteUser = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/CPanel/UserId?UserId=${selectedUser.userID}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        fetchUsers();
        setShowModal(false);
        setSnackbar({
          open: true,
          message: "User deleted successfully!",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to delete user",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbar({
        open: true,
        message: "Error deleting user",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    fetchClasses();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [classId, sectionId]);

  // Filter and paginate users
  const filteredUsers = users.filter(
    (user) =>
      user.studentName.toLowerCase().includes(search.toLowerCase()) ||
      user.userName.toLowerCase().includes(search.toLowerCase())
  );
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // if (!formId) {
  //   return (
  //     <Typography
  //       variant="h4"
  //       color="error"
  //       sx={{ textAlign: "center", mt: 2 }}
  //     >
  //       Invalid form ID
  //     </Typography>
  //   );
  // }

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Created Student Users page displays all student accounts that have been created in the system. Staff can search, filter, edit, and delete student user accounts from this page.",
    },
    {
      title: "🔍 Search & Filters",
      items: [
        "Search – Filter the list in real time by student name or username.",
        "Class – Filter students by class. Selecting a class enables the Section dropdown.",
        "Section – Further filter by section within the selected class.",
      ],
    },
    {
      title: "📊 Table Columns",
      items: [
        "Student Name – The full name of the student.",
        "User Name – The student's login username.",
        "Password – The student's password, hidden by default. Click the eye icon to reveal or hide it.",
        "Expiry Date – The date the student's account access expires.",
        "Action – Edit or Delete buttons based on your access rights.",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "Add User – Navigates to the Create New User page to create additional student accounts. Requires Edit permission.",
        "EDIT – Opens a dialog to update the student's password and expiry date. Requires Edit permission.",
        "DELETE – Permanently removes the student user account after confirmation. Requires Delete permission.",
      ],
    },
    {
      title: "✏️ Edit User Dialog",
      items: [
        "Student Name and User Name are read-only and cannot be changed.",
        "Password – Can be updated. Must be between 6 and 12 characters.",
        "Expiry Date – The date after which the student's login will expire.",
      ],
    },
    {
      title: "📄 Pagination",
      content:
        "The table shows 15 users per page. Use the pagination controls at the bottom to navigate between pages.",
    },
  ];

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: "#0B1F40",
          padding: "12px 24px",
          boxShadow: 3,
          borderRadius: "4px 4px 0 0",
          marginBottom: "15px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
          Created Student Users
        </Typography>

        {/* Info Icon Button */}
        <Tooltip title="View page information & guide" arrow>
          <IconButton
            onClick={() => setShowInfoModal(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 30,
              height: 30,
              ml: 0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(100,181,246,0.25)",
                borderColor: "#64B5F6",
                color: "#64B5F6",
                transform: "scale(1.1)",
              },
            }}
          >
            <InfoIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={3} style={{ padding: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          {/* Left Side: Search Bar + Dropdowns */}
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <TextField
              label="Search by Student Name or Username"
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
              style={{ width: "350px" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div style={{ display: "flex", gap: "15px" }}>
              <TextField
                select
                label="Class"
                value={classId}
                onChange={handleClassChange}
                style={{ width: "180px" }}
              >
                <MenuItem value="0">All Classes</MenuItem>
                {classes.length > 0 ? (
                  classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No Classes Available</MenuItem>
                )}
              </TextField>
              <TextField
                select
                label="Section"
                value={sectionId}
                onChange={handleSectionChange}
                style={{ width: "180px" }}
                disabled={sections.length === 0}
              >
                <MenuItem value="0">All Sections</MenuItem>
                {sections.length > 0 ? (
                  sections.map((section) => (
                    <MenuItem key={section.sectionId} value={section.sectionId}>
                      {section.sectionName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No Sections Available</MenuItem>
                )}
              </TextField>
            </div>
          </div>

          {/* Right Side: Add User Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/create-new-user")}
            disabled={!canEdit}
            style={{
              width: "120px",
              height: "50px",
              padding: "10px 0",
              fontWeight: "bold",
            }}
          >
            Add User
          </Button>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            <TableContainer component={Paper} style={{ marginTop: 16 }}>
              <Table
                sx={{
                  minWidth: 650,
                  border: "1px solid #ccc",
                  "& .MuiTableCell-root": {
                    borderRight: "1px solid #ccc",
                    fontSize: "1rem",
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    fontWeight: "bold",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                      Student Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                      User Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                      Password
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                      Expiry Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        textAlign: "center",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user, index) => (
                      <TableRow
                        key={user.userID}
                        sx={{ borderTop: "1px solid #ccc" }}
                      >
                        <TableCell sx={{ fontSize: "1rem" }}>
                          {user.studentName}
                        </TableCell>
                        <TableCell sx={{ fontSize: "1rem" }}>
                          {user.userName}
                        </TableCell>
                        <TableCell sx={{ fontSize: "1rem", display: "flex", alignItems: "center", gap: 1 }}>
                          {visiblePasswords.has(user.userID)
                            ? user.password
                            : user.password ? "•".repeat(user.password.length) : ""}
                          <IconButton
                            onClick={() => togglePasswordVisibility(user.userID)}
                            size="small"
                            aria-label="toggle password visibility"
                          >
                            {visiblePasswords.has(user.userID) ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontSize: "1rem" }}>
                          {user.expiryDate}
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontSize: "1rem" }}
                        >
                          <Tooltip
                            title={canEdit ? "" : "You are not authorized to edit"}
                            arrow
                          >
                            <span>
                              <Button
                                onClick={() => handleEditClick(index)}
                                variant="contained"
                                color="warning"
                                disabled={!canEdit}
                                sx={{ marginRight: 1 }}
                                startIcon={<EditIcon />}
                              >
                                EDIT
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={canDelete ? "" : "You are not authorized to delete"}
                            arrow
                          >
                            <span>
                              <Button
                                onClick={() => handleShowDeleteModal(user, index)}
                                variant="contained"
                                color="error"
                                disabled={!canDelete}
                                startIcon={<DeleteIcon />}
                              >
                                DELETE
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[15]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showModal && editUserIndex !== null && selectedUser.userID}
        onClose={() => setShowModal(false)}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 16px" }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: "#fff" }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: "20px" }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete the user{" "}
              <strong>{selectedUser.studentName}</strong>?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                onClick={() => setShowModal(false)}
                variant="outlined"
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteUser}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={showModal && editUserIndex !== null && !selectedUser.userID}
        onClose={handleModalClose}
        aria-labelledby="edit-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "54%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          {/* Header: Dark Navy Blue */}
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 16px" }}>
            <Typography
              id="edit-modal-title"
              variant="h6"
              sx={{ color: "#fff", fontWeight: "bold" }}
            >
              Edit User
            </Typography>
          </Box>

          {/* Content: Form Fields */}
          <Box sx={{ padding: "20px" }}>
            <TextField
              label="Student Name"
              fullWidth
              margin="dense"
              value={editedUser.studentName || ""}
              disabled
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="User Name"
              fullWidth
              margin="dense"
              value={editedUser.userName || ""}
              disabled
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="Password"
              fullWidth
              margin="dense"
              type={showPassword ? "text" : "password"}
              value={editedUser.password || ""}
              onChange={(e) => handleEditChange("password", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="Expiry Date"
              type="date"
              fullWidth
              margin="dense"
              value={editedUser.expiryDate || ""}
              onChange={(e) => handleEditChange("expiryDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            {/* Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                onClick={handleModalClose}
                variant="outlined"
                color="error"
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} variant="contained" color="primary">
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 560 },
            maxHeight: "85vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 8px 40px rgba(11,31,61,0.22)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: "#0B1F3D",
              padding: "14px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InfoIcon sx={{ color: "#64B5F6", fontSize: 22 }} />
              <Typography
                id="info-modal-title"
                variant="h6"
                sx={{ color: "#fff", fontWeight: 700 }}
              >
                Created Student Users — Page Guide
              </Typography>
            </Box>
           
            
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "#0B1F3D", mb: 0.8, fontSize: "0.97rem" }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#444", lineHeight: 1.7, fontSize: "0.875rem" }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{ fontSize: "0.875rem", color: "#444", lineHeight: 1.75, mb: 0.3 }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: "rgba(11,31,61,0.08)" }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: "12px 24px",
              borderTop: "1px solid rgba(11,31,61,0.1)",
              display: "flex",
              justifyContent: "flex-end",
              backgroundColor: "#f9fafb",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: "#0B1F3D",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#1a3560" },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <CustomAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </CustomAlert>
      </Snackbar>
    </Container>
  );
};

export default StudentUser;